import React, { useState, useEffect } from "react";
import {
  Modal, SpaceBetween, FormField, DatePicker, Input, Select, Textarea,
  Multiselect, Button, Box, Container, Header, Badge,
} from "@cloudscape-design/components";
import { MaintenanceEvent, UserInfo, ShiftTypeConfig } from "../types";
import { apiClient } from "../api/client";
import { CommentSection } from "./CommentSection";

interface Props {
  visible: boolean;
  onDismiss: () => void;
  onSave: () => void;
  event: MaintenanceEvent | null;
  defaultDate: string;
  siteId: string;
  user: UserInfo;
  isAdmin: boolean;
  availableTrades: string[];
  shiftTypes: ShiftTypeConfig[];
}

export const EventModal: React.FC<Props> = ({
  visible, onDismiss, onSave, event, defaultDate, siteId, user, isAdmin,
  availableTrades, shiftTypes,
}) => {
  const [title, setTitle] = useState(event?.title || "");
  const [date, setDate] = useState(event?.date || defaultDate || "");
  const [shift, setShift] = useState<string>(event?.shift || (shiftTypes[0]?.id || "day"));
  const [description, setDescription] = useState(event?.description || "");
  const [selectedTrades, setSelectedTrades] = useState<string[]>(event?.trades || []);
  const [saving, setSaving] = useState(false);

  const isEditing = !!event;

  // Build shift options from site config
  const shiftOptions = shiftTypes.map(st => ({ value: st.id, label: st.name }));
  const tradeOptions = availableTrades.map(t => ({ value: t, label: t }));

  const handleSave = async () => {
    if (!title || !date) return;
    setSaving(true);
    try {
      if (isEditing) {
        await apiClient.put(`/events/${event!.eventId}`, { title, date, shift, description, trades: selectedTrades });
      } else {
        await apiClient.post("/events", { siteId, title, date, shift, description, trades: selectedTrades });
      }
      onSave();
      onDismiss();
    } catch (err) {
      console.error("Failed to save event:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!event || !confirm("Delete this maintenance entry?")) return;
    try {
      await apiClient.delete(`/events/${event.eventId}`);
      onSave();
      onDismiss();
    } catch (err) {
      console.error("Failed to delete event:", err);
    }
  };

  // Get shift colour for display
  const getShiftColor = (shiftId: string) => {
    const st = shiftTypes.find(s => s.id === shiftId);
    return st?.color || "#888";
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={isEditing ? "Edit Maintenance" : "Add Maintenance"}
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {isEditing && isAdmin && (
              <Button variant="link" onClick={handleDelete}>Delete</Button>
            )}
            <Button onClick={onDismiss}>Cancel</Button>
            {isAdmin && (
              <Button variant="primary" onClick={handleSave} loading={saving}>
                {isEditing ? "Update" : "Create"}
              </Button>
            )}
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {isAdmin ? (
          <SpaceBetween size="m">
            <FormField label="Task Title">
              <Input value={title} onChange={({ detail }) => setTitle(detail.value)} placeholder="e.g. SLAM 03 PRINT CONV" />
            </FormField>
            <FormField label="Date">
              <DatePicker value={date} onChange={({ detail }) => setDate(detail.value)} placeholder="YYYY/MM/DD" />
            </FormField>
            <FormField label="Shift Type">
              <Select
                selectedOption={shiftOptions.find(o => o.value === shift) || shiftOptions[0]}
                options={shiftOptions}
                onChange={({ detail }) => setShift(detail.selectedOption.value || "day")}
              />
            </FormField>
            <FormField label="Trades" description="Select one or more trades for this task">
              <Multiselect
                selectedOptions={selectedTrades.map(t => ({ value: t, label: t }))}
                options={tradeOptions}
                onChange={({ detail }) => setSelectedTrades(detail.selectedOptions.map((o: any) => o.value))}
                placeholder="Select trades..."
                filteringType="auto"
              />
            </FormField>
            <FormField label="Description (optional)">
              <Textarea value={description} onChange={({ detail }) => setDescription(detail.value)} rows={3} />
            </FormField>
          </SpaceBetween>
        ) : (
          <SpaceBetween size="s">
            <Box variant="h3">{event?.title}</Box>
            <Box variant="p">Date: {event?.date}</Box>
            <Box variant="p">
              Shift: <span style={{ background: getShiftColor(event?.shift || ""), color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: "0.8rem" }}>
                {shiftTypes.find(s => s.id === event?.shift)?.name || event?.shift}
              </span>
            </Box>
            {event?.trades && event.trades.length > 0 && (
              <Box variant="p">
                Trades: {event.trades.map(t => <Badge key={t} color="blue">{t}</Badge>)}
              </Box>
            )}
            {event?.description && <Box variant="p">{event.description}</Box>}
          </SpaceBetween>
        )}

        {/* Comments section (shown for existing events) */}
        {isEditing && event && (
          <CommentSection
            eventId={event.eventId}
            user={user}
            isAdmin={isAdmin}
          />
        )}
      </SpaceBetween>
    </Modal>
  );
};
